import { EditOutlined } from "@ant-design/icons";
import { Button, Card, Col, Row, Spin, Table, Tag, Typography } from "antd";
import { ColumnsType } from "antd/es/table/interface";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import VuiButton from "../../../components/button";
import VuiFormItemView from "../../../components/form-item-view";
import VuiPageTitle from "../../../components/page-title";
import { useBreadcrumb } from "../../../context/breadcrumb";
import { BreadcrumbActionType } from "../../../context/breadcrumb/reducer";
import ModalExtend from "../components/modal-extend";
import { useFormDomain } from "../hooks/use-form-domain";
import { useGetDomain } from "../hooks/use-get-domain";
import { ExtendFormData, ExtendPayloadType } from "./interface";
import { DomainLog } from "../../../models/domain-log";
import VuiAccessible from "../../../components/accessible";
import VuiModalConfirmation from "../../../components/modal-confirmation";
import { openNotification } from "../../../utils/helpers";
import ExpiredStatus from "../../../components/expired-status";

const { Text, Title } = Typography;

const DomainDetailModule = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { loadDetailData, isOnFetchingDetailData, detailData } = useGetDomain();
  const title = `${t("common.text.domain")} | `;
  const { extendLoading, onExtend, onDelete, deleteLoading } = useFormDomain();
  const { dispatch } = useBreadcrumb();
  const [showExtendForm, setShowExtendForm] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  const domainLogColumns: ColumnsType<DomainLog> = [
    {
      title: t("common.text.description"),
      dataIndex: "action",
      key: "action",
      render: (text, record) => (
        <Text>
          {record.action !== "Cancel"
            ? `${record.action} (${moment(record.start_date).format(
                "DD MMM YYYY"
              )} - ${moment(record.expired_on).format("DD MMM YYYY")})`
            : `${record.action}`}
        </Text>
      ),
    },
    {
      title: t("common.text.actionDate"),
      dataIndex: "date",
      key: "date",
      render: (text) => <Text>{moment(text).format("DD MMM YYYY")}</Text>,
    },
    {
      title: t("common.text.by"),
      dataIndex: "user_id",
      key: "user_id",
      render: (text, record) => <Text>{record?.user?.name}</Text>,
    },
  ];

  useEffect(() => {
    dispatch({
      type: BreadcrumbActionType.Update,
      payload: [
        {
          link: "/",
          title: t("common.text.home"),
        },
        {
          link: "/domain",
          title: t("common.text.domain"),
        },
        {
          link: `/domain/${id}`,
          title: detailData?.name,
          isDisabled: true,
        },
      ],
    });
  }, [dispatch, t, detailData?.name, id]);

  useEffect(() => {
    if (id) {
      (async () => {
        await loadDetailData(id, {
          with: [
            "customer",
            "projectManager",
            "project",
            "status",
            "domainLogs.user",
            "registrar",
            "cdn",
          ],
        });
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleEdit = () => {
    navigate(`/domain/${id}/edit`);
  };

  const handleExtend = async (formData: ExtendFormData) => {
    const normalizeData: ExtendPayloadType = {
      action: formData?.action.label,
      duration: formData.duration,
    };

    if (id) {
      await onExtend(id, normalizeData);
      setShowExtendForm(false);
      await loadDetailData(id, {
        with: [
          "customer",
          "projectManager",
          "project",
          "status",
          "domainLogs.user",
          "registrar",
          "cdn",
        ],
      });
    }
  };

  const handleCloseModalDelete = useCallback(() => {
    setShowDeleteModal(false);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (id) {
      await onDelete(id).then(() => {
        setShowDeleteModal(false);
        openNotification(
          "success",
          t("notification.success.deleteItem", {
            item: t("common.text.domain"),
          })
        );

        navigate(`/maintenance`);
      });
    }
  }, [id, navigate, onDelete, t]);

  return (
    <>
      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <VuiPageTitle
            title={
              <Title level={4} style={{ fontWeight: 500, marginBottom: 0 }}>
                {title}
                <Text style={{ fontWeight: 800 }}>
                  {detailData?.name || ""}
                </Text>
              </Title>
            }
            onBackLink="/domain"
            rightTitleContent={[
              <ExpiredStatus
                label={detailData?.status.label}
                expiredIn={Number(detailData?.expired_in)}
              />,
            ]}
          >
            <VuiAccessible access="domain.update">
              {detailData?.can_update && (
                <Button
                  size="large"
                  type="primary"
                  ghost
                  icon={<EditOutlined />}
                  onClick={handleEdit}
                />
              )}
            </VuiAccessible>
            <VuiAccessible access="domain.extend">
              {detailData?.can_extend && (
                <VuiButton
                  title={t("common.button.extend")}
                  buttonProps={{
                    onClick: () => {
                      setShowExtendForm(true);
                    },
                  }}
                />
              )}
            </VuiAccessible>
          </VuiPageTitle>
        </Col>
      </Row>

      <Spin size="large" spinning={isOnFetchingDetailData}>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={16}>
            <Card
              title={t("common.text.projectInformation")}
              style={{ height: "100%" }}
            >
              <Row gutter={[16, 16]}>
                <Col xs={24} md={24}>
                  <VuiFormItemView
                    label={t("common.form.idProject.label")}
                    value={detailData?.project?.code}
                  />
                </Col>
                <Col xs={24} md={12}>
                  <VuiFormItemView
                    label={t("common.form.domainName.label")}
                    value={detailData?.name}
                  />
                </Col>
                <Col xs={24} md={12}>
                  <VuiFormItemView
                    label={t("common.form.projectManager.label")}
                    value={
                      detailData?.project_manager
                        ? `${detailData.project_manager.name} (${detailData.project_manager.initial})`
                        : ""
                    }
                  />
                </Col>
                <Col xs={24} md={12}>
                  <VuiFormItemView
                    label={t("common.form.registrationDate.label")}
                    value={moment(detailData?.date).format("DD MMM YYYY")}
                  />
                </Col>
                <Col xs={24} md={12}>
                  <VuiFormItemView
                    label={t("common.form.durationYear.label")}
                    value={`${detailData?.duration}`}
                  />
                </Col>
                <Col xs={24} md={12}>
                  <VuiFormItemView
                    label={t("common.form.registrarDomain.label")}
                    value={`${detailData?.registrar?.name}`}
                  />
                </Col>
                <Col xs={24} md={12}>
                  <VuiFormItemView
                    label={t("common.form.cdn.label")}
                    value={`${detailData?.cdn?.label}`}
                  />
                </Col>
              </Row>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card title={t("common.text.generalInformation")}>
              <Row gutter={[16, 16]}>
                <Col xs={24}>
                  <VuiFormItemView
                    label={t("common.form.client.label")}
                    value={detailData?.customer?.name}
                  />
                </Col>
                <Col xs={24}>
                  <VuiFormItemView
                    label={t("common.form.description.label")}
                    value={detailData?.description}
                  />
                </Col>
              </Row>
            </Card>
          </Col>
          <Col xs={24}>
            <Table
              rowKey={"id"}
              columns={domainLogColumns}
              dataSource={detailData?.domain_logs || []}
              loading={isOnFetchingDetailData}
              pagination={false}
              size="small"
              scroll={{ x: "max-content" }}
            />
          </Col>
          <VuiAccessible access="domain.destroy">
            {detailData?.can_delete && (
              <Col xs={24}>
                <Text
                  className="cursor-pointer"
                  onClick={() => setShowDeleteModal(true)}
                  title={t("common.text.delete")}
                  type="secondary"
                >
                  {t("common.text.delete")}
                </Text>
              </Col>
            )}
          </VuiAccessible>
        </Row>
      </Spin>

      <ModalExtend
        title={
          <>
            <Title
              level={5}
              style={{
                color: " #979DAD",
                fontWeight: 400,
              }}
            >
              Extend Domain
              <br />
              <Title level={4}>{detailData?.name}</Title>
            </Title>
          </>
        }
        show={showExtendForm}
        onClose={() => setShowExtendForm(false)}
        onSubmit={handleExtend}
        submitLoading={extendLoading}
      />
      <VuiModalConfirmation
        show={showDeleteModal}
        isLoading={deleteLoading}
        onSubmit={handleConfirmDelete}
        onCancel={handleCloseModalDelete}
      />
    </>
  );
};

export default DomainDetailModule;
